import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const useSubscriptionStatus = () => {
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    hasSubscription: false,
    hasPaymentMethod: false,
    status: null,
    subscription: null,
    isTrialing: false,
    isActive: false,
    loading: true,
    // 🆕 Helper functions for UI
    billingStatus: null,
    isPastDue: false,
    isUnpaid: false,
    isSuspended: false,
    canWrite: true,  // Can user perform write operations?
    canAccess: true  // Can user access the app at all?
  });

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setSubscriptionStatus(prev => ({ ...prev, loading: false }));
          return;
        }

        // 🔥 SUPERADMIN: Check from localStorage first (efficient)
        const userData = localStorage.getItem('user');
        let isSuperadmin = false;
        
        if (userData) {
          try {
            const user = JSON.parse(userData);
            isSuperadmin = user.role === 'superadmin';
          } catch (e) {
            console.error('Failed to parse user data:', e);
          }
        }
        
        // If Superadmin, grant full access immediately without subscription check
        if (isSuperadmin) {
          setSubscriptionStatus({
            hasSubscription: true,
            hasPaymentMethod: true,
            status: 'active',
            subscription: null,
            isTrialing: false,
            isActive: true,
            billingStatus: 'active',
            isPastDue: false,
            isUnpaid: false,
            isSuspended: false,
            canWrite: true,  // ✅ ALWAYS TRUE for superadmin
            canAccess: true, // ✅ ALWAYS TRUE for superadmin
            loading: false
          });
          return;
        }

        const response = await axios.get(
          `${API}/api/payments/subscription-status`,
          { headers: { Authorization: `Bearer ${token}` }}
        );

        const subscription = response.data.subscription;
        const billingStatus = subscription?.billing_status || 'active';
        const subscriptionStatus = subscription?.status;
        
        // Calculate access permissions for non-superadmin users
        const isPastDue = billingStatus === 'past_due';
        const isUnpaid = billingStatus === 'unpaid';
        const isSuspended = billingStatus === 'suspended';
        const canWrite = !isUnpaid && !isSuspended; // Read-only if unpaid, no access if suspended
        const canAccess = !isSuspended; // Only suspended blocks access
        
        // 🆕 FIX: User with active subscription (trialing, active) should have hasPaymentMethod = true
        const isPendingPayment = subscriptionStatus === 'pending_payment';
        const isActiveOrTrialing = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
        const hasPaymentMethod = !!subscription?.stripe_subscription_id || isPendingPayment || isActiveOrTrialing;
        
        setSubscriptionStatus({
          hasSubscription: response.data.has_subscription,
          hasPaymentMethod: hasPaymentMethod,
          status: subscriptionStatus,
          subscription: subscription,
          isTrialing: subscription?.status === 'trialing',
          isActive: subscription?.status === 'active',
          // 🆕 Billing status helpers
          billingStatus,
          isPastDue,
          isUnpaid,
          isSuspended,
          canWrite,
          canAccess,
          loading: false
        });
      } catch (error) {
        console.error('Error checking subscription:', error);
        setSubscriptionStatus(prev => ({ ...prev, loading: false }));
      }
    };

    checkSubscription();
    
    // Re-check on window focus
    window.addEventListener('focus', checkSubscription);
    return () => window.removeEventListener('focus', checkSubscription);
  }, []);

  return subscriptionStatus;
};
